import {
    fetchAllFormEntries,
    fetchFormsForAutocomplete,
    isEmpty,
    toSelectOptions,
    toStr,
} from "@rpforms/shared";
import * as actions from "@rpforms/shared/build/actions/formEntries";
import { Content } from "@rpforms/shared/build/components/layout/Content";
import { EmptySpace } from "@rpforms/shared/build/components/universal/EmptySpace";
import { Loader } from "@rpforms/shared/build/components/universal/Loader";
import PageNavAction from "@rpforms/shared/build/components/universal/PageNavAction";
import PageNavbar from "@rpforms/shared/build/components/universal/PageNavbar";
import { range } from "@rpforms/shared/build/utils";
import { push } from "connected-react-router";
import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Container, Form, Pagination, Row } from "react-bootstrap";
import { connect, useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import { useSearchParam } from "react-use";
import styled from "styled-components";
import { CustomerFilter } from "../../components/entries/CustomerFilter";
import { DeviceFilter } from "../../components/entries/DeviceFilter";
import { EntityFilter } from "../../components/entries/EntityFilter";
import EntryRow from "../../components/entries/EntryRow";
import {
    GenerateDocumentDropdown,
    getFormName,
    validateDocumentSelection,
} from "../../components/entries/GenerateDocumentDropdown";
import { FormFilter } from "../../components/entries/FormFilter";
import NotificationsFilter from "../../components/entries/NotificationsFilter";
import { StateFilter } from "../../components/entries/StateFilter";
import { GenerateTarget, useGenerateOffer } from "../../hooks/useGenerateOffer";
import { RootState } from "../../reducers";

const Centered = styled.div`
    display: flex;
    justify-content: center;
`;

const AllEntriesPage = () => {
    const formEntries = useSelector<RootState, any>((rootState) => rootState.formEntries);
    const entitiesDB = useSelector<RootState, any>((rootState) => rootState.entities);
    const [formsDB, setFormsDB] = useState([]);
    const {
        generate: generateOffer,
        isGenerating: isGeneratingOffer,
        error: offerError,
        setError: setOfferError,
        notesModal,
    } = useGenerateOffer();

    const pageParam = useSearchParam("page");
    const pageSizeParam = useSearchParam("page_size");

    const idsParams = useSearchParam("ids");
    const fromParam = useSearchParam("from");
    const untilParam = useSearchParam("until");
    const devicesParam = useSearchParam("device_ids");
    const formParam = useSearchParam("form_ids");
    const stateParam = useSearchParam("states");
    const hasNotificationsParam = useSearchParam("has_notifications");
    const customerParam = useSearchParam("customer_ids");
    const entityParam = useSearchParam("entity_ids");

    const field = useSearchParam("field") || "";
    const fieldValue = useSearchParam("fieldValue") || null;
    const fieldComparator = useSearchParam("fieldComparator") || null;

    useEffect(() => {
        dispatch(
            fetchFormsForAutocomplete((data) => {
                setFormsDB(data);
            })
        );
    }, []);

    const getInitialParameter = (p, n, select = true) => {
        const sp = select ? toSelectOptions(toStr(p).split(",")) : p;
        if (!isEmpty(sp)) {
            return sp;
        }
        try {
            const ret = JSON.parse(sessionStorage.getItem(n));
            if (ret == null && ["form_entries.from", "form_entries.until"].includes(n)) {
                return "";
            }
            return ret;
        } catch (e) {
            if (["form_entries.from", "form_entries.until"].includes(n)) {
                return "";
            }
            return null;
        }
    };

    const setInitialParameter = (n, v) => {
        sessionStorage.setItem(n, JSON.stringify(v));
    };

    const unsetInitialParameters = () => {
        sessionStorage.removeItem("form_entries.from");
        sessionStorage.removeItem("form_entries.until");
        sessionStorage.removeItem("form_entries.devices");
        sessionStorage.removeItem("form_entries.forms");
        sessionStorage.removeItem("form_entries.has_notifications");
        sessionStorage.removeItem("form_entries.states");
        sessionStorage.removeItem("form_entries.customers");
        sessionStorage.removeItem("form_entries.entities");
    };

    const initialPageSize = parseInt(pageSizeParam, 10) || 10;
    const initialPage = parseInt(pageParam, 10) || 1;
    const [page, setPage] = useState(getInitialParameter(initialPage, "form_entries.page", false));
    const [pageSize, setPageSize] = useState(
        getInitialParameter(initialPageSize, "form_entries.page_size", false)
    );

    const [ids, setIds] = useState(getInitialParameter(idsParams, "form_entries.ids", false));
    const [from, setFrom] = useState(getInitialParameter(fromParam, "form_entries.from", false));
    const [until, setUntil] = useState(
        getInitialParameter(untilParam, "form_entries.until", false)
    );
    const [devices, setDevices] = useState(
        getInitialParameter(devicesParam, "form_entries.devices")
    );
    const [forms, setForms] = useState(getInitialParameter(formParam, "form_entries.forms"));
    const [states, setStates] = useState(getInitialParameter(stateParam, "form_entries.states"));
    const [hasNotifications, setHasNotifications] = useState(
        !!getInitialParameter(hasNotificationsParam, "form_entries.has_notifications", false)
    );
    const [customers, setCustomers] = useState(
        getInitialParameter(customerParam, "form_entries.customers")
    );
    const [entities, setEntities] = useState(
        getInitialParameter(entityParam, "form_entries.entities")
    );

    // Selection state for offer generation
    const [selectedEntryIds, setSelectedEntryIds] = useState<number[]>([]);
    const [selectedEntriesData, setSelectedEntriesData] = useState<Map<number, any>>(new Map());

    const dispatch = useDispatch();

    const [currentUrl, setCurrentUrl] = useState("/entries");

    const [resetted, setResetted] = useState(false);

    useEffect(() => {
        if (resetted) {
            setResetted((prevState) => false);
            return;
        }
        const opts: any = {
            hidden_fields:
                "disposal_sales_price,disposal_sales_price_formatted,repair_sales_price,repair_sales_price_formatted,service_sales_price,service_sales_price_formatted,total_sales_price,total_sales_price_formatted,form_data,uploads,latest_revision_version,latest_revision_id",
        };
        let url = `/entries?page=${page}&page_size=${pageSize}`;
        if (ids && ids.length > 0) {
            const idsArr = ids.split(",").filter((x) => x != null);
            url += `&ids=${idsArr.join(",")}`;
            opts["q[id_in][]"] = idsArr;
        }
        if (from) {
            url += `&from=${from}`;
            opts["q[created_at_gteq]"] = from;
        }
        if (until) {
            url += `&from=${until}`;
            opts["q[created_at_lteq]"] = `${until} 23:59:59`;
        }
        if (devices && devices.length > 0) {
            const deviceIds = devices.map((d) => d.value);
            url += `&device_ids=${deviceIds.join(",")}`;
            if (deviceIds.length === 1 && deviceIds[0] === "-1") {
                opts["q[device_id_blank]"] = true;
            } else {
                opts["q[device_id_in][]"] = deviceIds;
            }
        }
        if (forms && forms.length > 0) {
            const formIds = forms.map((d) => d.value);
            url += `&form_ids=${formIds.join(",")}`;
            opts["q[form_id_in][]"] = formIds;
        }
        if (states && states.length > 0) {
            const stateIds = states.map((d) => d.value);
            url += `&states=${stateIds.join(",")}`;
            opts["state_in[]"] = stateIds;
        }
        if (customers && customers.length > 0) {
            const customerIds = customers.map((d) => d.value);
            url += `&customer_ids=${customerIds.join(",")}`;
            opts["q[customer_id_in][]"] = customerIds;
        }
        if (entities && entities.length > 0) {
            const entityIds = entities.map((d) => d.value);
            url += `&entity_ids=${entityIds.join(",")}`;
            opts["q[entity_id_in][]"] = entityIds;
        }
        if (hasNotifications) {
            url += `&has_notifications=${hasNotifications}`;
            opts.has_notifications = true;
        }
        if (field) {
            url += `&field=${field}`;
            opts.field = field;
            if (fieldValue) {
                url += `&fieldValue=${fieldValue}`;
                opts.fieldValue = fieldValue;
                if (!fieldComparator) {
                    url += `&fieldComparator=eq`;
                    opts.fieldComparator = "eq";
                }
            } else {
                if (!fieldComparator) {
                    url += `&fieldComparator=exists`;
                    opts.fieldComparator = "exists";
                }
            }
            if (fieldComparator) {
                url += `&fieldComparator=${fieldComparator}`;
                opts.fieldComparator = fieldComparator;
            }
        }
        if (currentUrl !== url) {
            setCurrentUrl(url);
            dispatch(push(url));
            dispatch(fetchAllFormEntries(page, () => {}, true, opts, pageSize));
        }
    }, [
        ids,
        page,
        pageSize,
        from,
        until,
        devices,
        forms,
        states,
        hasNotifications,
        customers,
        entities,
        resetted,
    ]);

    const openPage = (newPage) => {
        setPage(newPage);
    };

    const pages = () => {
        const pagesOffset = 5;
        return [1, ...range(Math.max(2, page - pagesOffset), page + pagesOffset)];
    };

    const resetFilter = () => {
        setResetted((prevState) => {
            unsetInitialParameters();
            setIds(null);
            setFrom("");
            setUntil("");
            setDevices(null);
            setForms(null);
            setHasNotifications(false);
            setStates(null);
            setCustomers(null);
            setEntities(null);
            return true;
        });
    };

    // Selection handlers for offer generation
    const handleSelectEntry = (entryId: number, isSelected: boolean, entry?: any) => {
        if (isSelected) {
            setSelectedEntryIds([...selectedEntryIds, entryId]);
            if (entry) {
                setSelectedEntriesData((prev) => {
                    const newMap = new Map(prev);
                    newMap.set(entryId, { formEntry: entry });
                    return newMap;
                });
            }
        } else {
            setSelectedEntryIds(selectedEntryIds.filter((id) => id !== entryId));
            setSelectedEntriesData((prev) => {
                const newMap = new Map(prev);
                newMap.delete(entryId);
                return newMap;
            });
            // Clear error when deselecting
            if (selectedEntryIds.length <= 1) {
                setOfferError(null);
            }
        }
    };

    const handleSelectAll = () => {
        const allIds = formEntries.list.map((entry) => entry.id);
        const allSelected = allIds.every((id) => selectedEntryIds.includes(id));

        if (allSelected) {
            // Deselect all
            setSelectedEntryIds([]);
            setSelectedEntriesData(new Map());
            setOfferError(null);
        } else {
            // Select all visible entries
            setSelectedEntryIds(allIds);

            // Cache all entry data
            setSelectedEntriesData((prev) => {
                const newMap = new Map(prev);
                formEntries.list.forEach((entry) => {
                    if (!newMap.has(entry.id)) {
                        newMap.set(entry.id, { formEntry: entry });
                    }
                });
                return newMap;
            });
        }
    };

    const handleGenerateDocument = async (target: GenerateTarget = "offer") => {
        const validationError = validateDocumentSelection(
            target,
            selectedEntryIds.map((id) => selectedEntriesData.get(id)?.formEntry)
        );
        if (validationError) {
            setOfferError(validationError);
            return;
        }

        const cache = new Map(selectedEntriesData);
        selectedEntryIds.forEach((id) => {
            if (!cache.get(id)?.formEntry) {
                const listed = formEntries.list.find((entry) => entry.id === id);
                if (listed) {
                    cache.set(id, { ...cache.get(id), formEntry: listed });
                }
            }
        });

        await generateOffer(target, selectedEntryIds, {
            cache,
            forms: formsDB,
            fallbackCustomerId:
                !isEmpty(customerParam) && Array.isArray(customers) && customers.length > 0
                    ? customers[0].value
                    : null,
            fallbackEntityId:
                !isEmpty(entityParam) && Array.isArray(entities) && entities.length > 0
                    ? entities[0].value
                    : null,
            onEntryLoaded: (entryId, formEntry, formPayload) =>
                setSelectedEntriesData((prev) => {
                    const newMap = new Map(prev);
                    newMap.set(entryId, { formEntry, formPayload });
                    return newMap;
                }),
        });
    };

    return (
        <>
            {notesModal}
            <PageNavbar
                title="Protokolle"
                description="Hier können Sie übertragene Formulare verwalten."
            >
                {!formEntries.isLoading && (
                    <>
                        <Container>
                            <Row
                                style={{
                                    alignItems: "flex-end",
                                    width: "600px",
                                }}
                            ></Row>
                        </Container>
                        {selectedEntryIds.length > 0 && (
                            <GenerateDocumentDropdown
                                selectedEntries={selectedEntryIds.map(
                                    (id) => selectedEntriesData.get(id)?.formEntry
                                )}
                                isGenerating={isGeneratingOffer}
                                onGenerate={handleGenerateDocument}
                            />
                        )}
                        <PageNavAction
                            onClick={() => {
                                const offerForm = formsDB.find(
                                    (f) => f.name === "000_Angebot" || f.label === "000_Angebot"
                                );
                                if (!offerForm) {
                                    return;
                                }
                                const formValue = [{ value: offerForm.id, label: offerForm.name }];
                                const stateValue = [{ value: "accepted", label: "Akzeptiert" }];
                                setInitialParameter("form_entries.forms", formValue);
                                setForms(formValue);
                                setInitialParameter("form_entries.states", stateValue);
                                setStates(stateValue);
                            }}
                            primary={false}
                            icon="check"
                            title="Angebote Roland"
                        />
                        <PageNavAction
                            link="/entries/editor"
                            primary={true}
                            icon="plus"
                            title="Protokoll erstellen"
                        />
                    </>
                )}
            </PageNavbar>
            <div>
                {offerError && (
                    <Alert variant="danger" dismissible onClose={() => setOfferError(null)}>
                        {offerError}
                    </Alert>
                )}
                {selectedEntryIds.length > 0 && (
                    <Alert variant="info">
                        <strong>Ausgewählt:</strong> {selectedEntryIds.length} Formular
                        {selectedEntryIds.length > 1 ? "e" : ""}
                        {(() => {
                            const selectedForms = selectedEntryIds
                                .map(
                                    (id) =>
                                        selectedEntriesData.get(id)?.formEntry ||
                                        formEntries.list.find((e) => e.id === id)
                                )
                                .filter(Boolean);
                            const formNames = selectedForms.map((entry) => getFormName(entry));
                            const uniqueFormNames = Array.from(new Set(formNames));

                            if (uniqueFormNames.length === 1) {
                                const formName = uniqueFormNames[0] || "Unbekannt";
                                return (
                                    <span style={{ marginLeft: "10px" }}>(Typ: {formName})</span>
                                );
                            } else if (uniqueFormNames.length > 1) {
                                return (
                                    <span style={{ marginLeft: "10px" }}>
                                        (Formulartypen: {uniqueFormNames.join(", ")} – Positionen
                                        werden je Typ gruppiert)
                                    </span>
                                );
                            }
                            return null;
                        })()}
                    </Alert>
                )}
                <Content>
                    <Row style={{ paddingBottom: "20px" }}>
                        <Col>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                }}
                            >
                                <label
                                    style={{ minWidth: "20%", paddingTop: "6px" }}
                                    htmlFor={"date-from"}
                                >
                                    Von:
                                </label>
                                <input
                                    style={{ width: "auto" }}
                                    id={"date-from"}
                                    type={"date"}
                                    name={"created_at.gte"}
                                    className="form-control"
                                    onInput={(v) => {
                                        setInitialParameter(
                                            "form_entries.from",
                                            (v.target as any).value
                                        );
                                        setFrom((v.target as any).value);
                                    }}
                                    onChange={() => {}}
                                    value={from}
                                />
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                }}
                            >
                                <label
                                    style={{ minWidth: "20%", paddingTop: "6px" }}
                                    htmlFor={"date-until"}
                                >
                                    Bis:
                                </label>
                                <input
                                    style={{ width: "auto" }}
                                    id={"date-until"}
                                    type={"date"}
                                    name={"created_at.lteq"}
                                    className="form-control"
                                    onInput={(v) => {
                                        setInitialParameter(
                                            "form_entries.until",
                                            (v.target as any).value
                                        );
                                        setUntil((v.target as any).value);
                                    }}
                                    onChange={() => {}}
                                    value={until}
                                />
                            </div>
                        </Col>
                        <Col>
                            <DeviceFilter
                                onSelect={(v) => {
                                    setInitialParameter("form_entries.devices", v);
                                    setDevices(v);
                                }}
                                value={devices}
                                isMulti
                                isSearchable
                            ></DeviceFilter>
                        </Col>
                        <Col>
                            <FormFilter
                                onSelect={(v) => {
                                    setInitialParameter("form_entries.forms", v);
                                    setForms(v);
                                }}
                                value={forms}
                                isMulti
                                isSearchable
                            />
                            <NotificationsFilter
                                onCheck={(e) => {
                                    const hasN = e.target.checked;
                                    setInitialParameter("form_entries.has_notifications", hasN);
                                    setHasNotifications(hasN);
                                }}
                                value={hasNotifications}
                            />
                        </Col>
                        <Col>
                            <StateFilter
                                onSelect={(v) => {
                                    setInitialParameter("form_entries.states", v);
                                    setStates(v);
                                }}
                                value={states}
                                isMulti
                                isSearchable
                            />
                        </Col>
                        <Col>
                            <CustomerFilter
                                onSelect={(v) => {
                                    setInitialParameter("form_entries.customers", v);
                                    setCustomers(v);
                                }}
                                value={customers}
                                isMulti
                                isSearchable
                            />
                            <EntityFilter
                                onSelect={(v) => {
                                    setInitialParameter("form_entries.entities", v);
                                    setEntities(v);
                                }}
                                optionsFilter={(entityOptions) => {
                                    if (isEmpty(customers)) {
                                        return entityOptions;
                                    }
                                    const customerIds = customers.map((c) => parseInt(c.value, 10));
                                    return entityOptions.filter((o) => {
                                        const entity = entitiesDB.autocomplete.find(
                                            (e) => e.id === o.value
                                        );
                                        return (
                                            entity &&
                                            customerIds.includes(parseInt(entity.customer_id, 10))
                                        );
                                    });
                                }}
                                value={entities}
                                isMulti
                                isSearchable
                            />
                        </Col>
                        <Col style={{ textAlign: "right" }}>
                            <Button onClick={resetFilter}>Filter zurücksetzen</Button>
                        </Col>
                    </Row>
                    {formEntries.isLoading && <Loader />}
                    {!formEntries.isLoading && formEntries.list.length > 0 && (
                        <div>
                            <div
                                style={{
                                    padding: "10px",
                                    backgroundColor: "#f8f9fa",
                                    marginBottom: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                }}
                            >
                                <Form.Check
                                    type="checkbox"
                                    checked={
                                        formEntries.list.length > 0 &&
                                        formEntries.list.every((entry) =>
                                            selectedEntryIds.includes(entry.id)
                                        )
                                    }
                                    onChange={handleSelectAll}
                                    label="Alle auswählen"
                                />
                                {selectedEntryIds.length > 0 && (
                                    <span className="text-muted">
                                        {selectedEntryIds.length} ausgewählt
                                    </span>
                                )}
                            </div>
                            {formEntries.list.map((entry) => {
                                return (
                                    <EntryRow
                                        entry={entry}
                                        key={entry.id}
                                        isSelected={selectedEntryIds.includes(entry.id)}
                                        onSelect={(isSelected) =>
                                            handleSelectEntry(entry.id, isSelected, entry)
                                        }
                                    />
                                );
                            })}
                        </div>
                    )}
                </Content>
            </div>

            {!formEntries.isLoading && formEntries.list.length === 0 && (
                <Content>
                    <EmptySpace
                        title={"Keine Protokolle gefunden."}
                        description={
                            "Übertrage ein ausgefülltes Formular mit einem iPad damit es hier erscheint."
                        }
                        cta={false}
                    />
                </Content>
            )}
            <div>
                <Centered>
                    <Pagination>
                        {pages().map((p) => {
                            return (
                                <Pagination.Item
                                    active={p === page}
                                    key={`page_${p}`}
                                    onClick={() => openPage(p)}
                                >
                                    {p}
                                </Pagination.Item>
                            );
                        })}
                    </Pagination>
                </Centered>
            </div>
        </>
    );
};

function mapStateToProps(state) {
    return { device: state.device, formEntries: state.formEntries };
}

export default withRouter(connect(mapStateToProps, actions)(AllEntriesPage));
